<script lang="ts">
  import {STEP} from '@/types/common';
  import {onDestroy, onMount} from 'svelte';
  import {getInstance} from '@/services/animation';
  import {thumbnail, step, download, downloadDialog, isMobile, thumbDraft} from '@/store';
  import {Container} from '@components';
  import PreviewArea from './PreviewArea.svelte';
  import previewAreaLoadingStore, {removingBg} from './loading-store';
  import editorStore from '../../lib/editor/editor.store';
  import EditorStyleView from '../../lib/editor/StyleView/EditorStyleView.svelte';
  import {fly} from 'svelte/transition';
  import NewPreviewSidebar from '../../components/thumbnail-sidebar/NewPreviewSidebar.svelte';
  import ExtendedOptionMenu from '../../components/thumbnail-sidebar/ExtendedOptionMenu.svelte';
  import {wait} from '../../utils/common';
  import {topnav} from '@/utils/top-nav-next-custom-events';
  import DownloadDialog from './DownloadDialog.svelte';
  import VidImgDialog from './VidImgDialog.svelte';
  import {httpClient} from '@/utils/http-client';
  import {prepareDownload} from '@/lib/editor/download';
  import Swal from 'sweetalert2';
  import Toolbar from '../../lib/editor/Toolbar/Toolbar.svelte';
  import ActiveElementToolbar from '../../lib/editor/ActiveElementToolbar/ActiveElementToolbar.svelte';
  import PopupBox from '../../components/thumbnail-sidebar/PopupBox/PopupBox.svelte';
  import {draftName} from '@/store/drafts';

  let progressBar: any;
  let zoomPercent = 1;
  let progress = 0;
  let unsubs: Function;
  // let isVidImgDialogOpen = true;
  const animationService = getInstance();

  const updateNextStep = () => step.update(STEP.RENDER);
  const updatePrevStep = () => step.update(STEP.CHOOSE_TEMPLATE);

  const cleanupHighlights = () => {
    const highlightedElements = document.querySelectorAll('.highlighted-element');
    highlightedElements.forEach(element => {
      try {
        element.classList.remove('highlighted-element');
      } catch (err) {
        console.warn('Could not remove classlist', err);
        console.trace();
      }
    });
  };

  let isDownloadDialogOpen = false;
  let title = '';
  $: title = $thumbnail?.video?.title || 'Untitled Project';

  let downloadStarted = false;

  async function startDownload(dom: string, fileType: string = 'png', isSermonSend: boolean = false) {
    let height = 0;
    let width = 0;

    const el = document.getElementById('templateLoaded');
    if (el) {
      const ofy = el.getAttribute('y');
      const ofx = el.getAttribute('x');
      height = ofy ? Math.round(Number(ofy)) : 0;
      width = ofx ? Math.round(Number(ofx)) : 0;
    }
    //API Flash has max height of 4320 unless we use full_page=true
    //only set full_page=true when needed because it can slow down the process
    if (height > 4320) {
      var fullPage = 'true';
      height = 4000;
    } else {
      var fullPage = 'false';
    }

    let loading = true;
    let error = '';

    try {
      download.updateProgress(60);
      const response = await httpClient.post(
        '/thumbnail/download',
        {projectId: $thumbDraft.currentCloudDraftId, dom, fileType, height, fullPage, width},
        {
          responseType: 'blob'
        }
      );
      download.updateProgress(80);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${$draftName.replace('.', '(dot)') + '.' + fileType}`);
      document.body.appendChild(link);
      download.updateProgress(100);
      if (isSermonSend) {
        Swal.fire({
          icon: 'success',
          title: 'Exported to Sermon Send',
          showConfirmButton: false,
          timer: 1500,
          toast: true,
          position: 'bottom-end'
        });
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        Swal.fire({
          icon: 'success',
          title: 'Image Downloaded',
          showConfirmButton: false,
          timer: 10000,
          toast: true,
          position: 'bottom-end'
        });
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Download',
        showConfirmButton: false,
        timer: 10000,
        toast: true,
        position: 'bottom-end'
      });
    }
  }

  const saveProject = async (e: Event) => {
    downloadStarted = true;
    downloadDialog.set(true);
    if ($thumbDraft.currentCloudDraftId) {
      thumbDraft.markDraftAsProject($draftName, $thumbDraft.currentCloudDraftId);
    }

    const dom = animationService.getDom().parentElement?.innerHTML;
    cleanupHighlights();
    if (dom) {
      download.updateProgress(20);
      let postDom = await prepareDownload(dom, 'outerHTML', () => {});
      download.updateProgress(50);
      startDownload(postDom, (e as any).detail.fileType, (e as any).detail.isSermonSend);
    }
    download.reset();
    // stopVideo();
    await wait(500);

    if (!$thumbnail.video) throw 'Video undefined.';
    if (!$thumbnail.template) throw 'Template undefined.';
  };

  const downloadDialogIsOpenSubscription = downloadDialog.subscribe(value => {
    isDownloadDialogOpen = value;
  });

  function resetDownload() {
    downloadStarted = false;
  }

  onMount(() => {
    window.addEventListener('reset-download', resetDownload);
    topnav.next(updateNextStep);
    topnav.previous(updatePrevStep);
    topnav.download(saveProject);
    editorStore.removePopoverComponent();
    // setAnimationControllerLoading(true);
    previewAreaLoadingStore.set(true);
    unsubs = download.subscribe(prog => {
      if (progressBar) {
        let bar = progressBar.getProgressElement();
        bar.value = prog;
        progressBar.changeRunnerColor(bar);
      }
      if (prog.progress !== 0) progress = prog.progress;
    });
    animationService.onUpdate(prog => {
      if (progressBar) {
        let bar = progressBar.getProgressElement();
        bar.value = prog;
        progressBar.changeRunnerColor(bar);
      }
      progress = prog;
    });
  });

  onDestroy(() => {
    window.removeEventListener('reset-download', resetDownload);
    downloadDialogIsOpenSubscription();
    animationService.reset();
    download.reset();
    unsubs();
  });
</script>

{#if $editorStore.active}
  <div id="preview" />
{/if}
<div id="customCreatorCss">
  <style>
    body {
      background-color: #fafafa;
      overflow: hidden;
    }

    .stampImg {
      width: 100%;
      height: 100%;
      object-fit: fill;
    }

    #bkgImageContainter {
      height: 100%;
      position: relative;
    }

    .hidden {
      display: none !important;
    }

    .visible {
      display: inherit;
    }

    .templateimg {
      max-height: 100%;
      width: auto;
    }

    /* #userText{
        font-family: Fjalla One;
        } */

    canvas {
      width: 100%;
      margin: 0 auto;
      float: none;
      display: block;
      background-repeat: no-repeat;
      background-position: center;
    }

    #tempHide,
    .userTextClass {
      display: none;
    }

    .dynamic {
      padding: 15px;
    }

    .bookImg {
      width: 100% !important;
      height: 100% !important;
    }

    #bkgimage {
      position: relative;
      float: left;
    }

    #bkgimage:after {
      content: ' ';
      display: block;
      clear: both;
    }

    #preview {
      width: 100%;
    }

    .centerobject {
      margin: 0 auto;
      display: block;
      float: none;
    }

    .close-button {
      top: 15px;
      right: 15px;
      display: none;
    }

    .close-button-np {
      display: block;
      top: 0;
      right: 0;
      z-index: 9;
    }

    .fixedHeight {
      height: 150px;
    }

    .stopClicks {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: rgba(0, 0, 0, 0.8);
      z-index: 1;
    }

    .ui-icon {
      background-image: none;
    }

    .dynamic p {
      font-size: inherit;
      line-height: inherit;
      padding-bottom: 0;
      font-family: inherit;
      font-weight: inherit;
      margin: 0;
    }

    .dynamic {
      white-space: pre-wrap;
    }

    .ql-picker-label {
      border-radius: 5px;
      background-color: #dadada;
      text-align: left;
      overflow: hidden;
    }

    .ql-font {
      margin-top: 5px;
    }

    .ql-snow .ql-icon-picker,
    .ql-snow.ql-toolbar button {
      height: 20px;
      width: 24px;
    }

    .dynamic em,
    .dynamic strong,
    .dynamic s,
    .dynamic u {
      font-family: inherit;
    }

    .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
      max-height: 300px;
      overflow-y: scroll;
    }

    .bkgStyles {
      transition:
        visibility 0s,
        opacity 0.5s linear;
      transition: all 0.5s linear;
    }

    .sliderVert {
      transform: rotate(270deg);
      width: 200px !important;
      z-index: 99;
      top: 110px;
      position: relative;
      right: 70px;
      padding: 1px;
      outline: solid 20px #fff;
      opacity: 1;
      box-shadow: 0 0 50px #333;
      border-radius: 0;
      margin-bottom: -50px !important;
    }

    .grayButton {
      border-radius: 5px;
      background-color: #dadada !important;
      max-width: 150px;
    }

    .whiteButton {
      width: 100% !important;
      padding: 15px !important;
      height: auto !important;
      background-color: #fff !important;
      border: solid 2px #dadada !important;
      margin: 0 auto;
      border-radius: 5px;
    }

    .whiteButton:hover {
      background-color: rgb(98, 150, 190) !important;
      color: #fff !important;
    }

    #canvasOverlay {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      background: url(/wp-content/uploads/CSB-logo-text-and-img-1153x500.png);
      opacity: 0.2;
      background-repeat: no-repeat;
      background-position: center;
    }

    .newTemplate {
      position: absolute;
      top: 0;
      left: 0;
      width: 50px;
    }

    #bkgimageOverlay {
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
    }

    #overlay {
      width: 100%;
      height: 100%;
    }

    #finalCroppedImage {
      max-width: 50%;
      margin: 0 auto;
      float: none;
      display: block;
      width: auto;
    }

    #editableStyles .col-md-6 {
      padding-right: 3px;
      padding-left: 3px;
    }

    .resultImg {
      /* margin-bottom: 5px; */
      border-radius: 6px;
    }

    .straightLine,
    .hrLine {
      position: absolute;
      background-color: red;
      transition: transform 0.05s ease-in-out;
      pointer-events: none;
    }

    .ui-resizable-se.large-handle,
    #finalImageToExport:hover .ui-resizable-se.large-handle {
      border-bottom: #fe7d67 5px solid;
      border-right: #fe7d67 5px solid;
      right: 3px;
      bottom: 3px;
      cursor: se-resize;
    }

    .searchDiv {
      border-radius: 5px;
      border: solid 1px #dadada;
      width: 100%;
      text-align: left;
      height: 52px;
      box-shadow: 0 0 10px #999;
    }

    .searchField {
      outline: none;
      width: 80%;
      float: left !important;
      border: none;
      text-align: left !important;
      min-height: 50px;
      font-size: 16px;
      padding: 5px;
    }

    .comTemplate {
      position: relative;
    }

    .custom-tooltip + .tooltip > .tooltip-inner {
      background-image: -moz-linear-gradient(135deg, rgba(76, 0, 204, 1) 0%, rgba(255, 0, 57, 1) 100%) !important;
      background-image: -webkit-linear-gradient(135deg, rgba(76, 0, 204, 1) 0%, rgba(255, 0, 57, 1) 100%) !important;
      background-image: -ms-linear-gradient(135deg, rgba(76, 0, 204, 1) 0%, rgba(255, 0, 57, 1) 100%) !important;
      background-image: linear-gradient(135deg, rgba(76, 0, 204, 1) 0%, rgba(255, 0, 57, 1) 100%) !important;
      font-size: 14px;
      z-index: 999999999;
    }

    .ql-align-justify {
      text-align: justify;
    }

    .ql-align-right {
      text-align: right;
    }

    .ql-align-center {
      text-align: center;
    }

    .coverStamp .stampImg {
      padding: 0 !important;
    }

    #freeUserWatermark {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 7;
      pointer-events: none;
    }

    #finalImageToExport video {
      width: 100%;
    }

    #finalImageToExport {
      position: relative;
    }

    #imageBorder {
      position: absolute;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 2;
    }

    .individualLetter {
      font-family: inherit;
      vertical-align: top;
      display: inline-block;
    }

    .backgroundClipText {
      background-clip: text !important;
      -webkit-background-clip: text !important;
      -moz-background-clip: text !important;
      -o-background-clip: text !important;
    }

    .backgroundVideoWrapper {
      width: 100%;
      position: absolute;
    }

    .backgroundImageWrapper img {
      width: 100%;
    }

    .shapeOuter {
      position: absolute;
    }

    .maskElement-top {
      -webkit-mask-image: -webkit-gradient(linear, left top, left bottom, from(rgb(0, 0, 0)), to(rgba(0, 0, 0, 0)));
      mask-image: linear-gradient(to bottom, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
    }

    .maskElement-left {
      -webkit-mask-image: -webkit-gradient(linear, left top, right top, from(rgb(0, 0, 0)), to(rgba(0, 0, 0, 0)));
      mask-image: linear-gradient(to right, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
    }

    .maskElement-bottom {
      -webkit-mask-image: -webkit-gradient(linear, left top, left bottom, from(rgba(0, 0, 0, 0)), to(rgb(0, 0, 0)));
      mask-image: linear-gradient(to top, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
    }

    .maskElement-right {
      -webkit-mask-image: -webkit-gradient(linear, left top, right top, from(rgba(0, 0, 0, 0)), to(rgb(0, 0, 0)));
      mask-image: linear-gradient(to left, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
    }

    pwa-editor-bar-cnt,
    pwa-container-wrapper {
      display: none !important;
    }
  </style>
</div>

<Container className="flex overflow-hidden">
  {#if !$isMobile}
    {#if $editorStore.stylebarActive}
      <div class="relative flex w-3/12 flex-col gap-6 bg-slate-400 bg-opacity-10">
        <EditorStyleView />
      </div>
    {:else}
      <NewPreviewSidebar />
    {/if}
  {/if}

  <!-- <RenderManager /> -->
  <div class="relative h-full w-full">
    {#if $isMobile}
      <div class="w-full bg-gradient-to-r from-primary to-secondary py-2 text-center text-xs text-white">
        To edit, kindly open your project from your desktop.
      </div>
      <div class="absolute left-0 h-full w-full opacity-0" style="z-index:50;"></div>
    {/if}
    {#if !$isMobile}
      <ExtendedOptionMenu />
      <Toolbar />
      <PopupBox />
    {/if}

    <div class="relative h-full flex-grow overflow-hidden bg-slate-500 bg-opacity-10" id="thumbArea">
      <!-- <div class="h-full w-full block bg-slate-500 bg-opacity-10"> -->

      <!-- <ActiveElementToolbar/> -->
      <PreviewArea />
      <!-- </div> -->
      <div in:fly={{y: 100, duration: 300}} out:fly={{y: 100, duration: 300}} class="w-full flex-shrink-0"></div>
    </div>
  </div>
</Container>

<DownloadDialog bind:isDownloadDialogOpen bind:progress />
{#if !$isMobile}
  <VidImgDialog />
{/if}
{#if $previewAreaLoadingStore}
  <div class="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-zinc-900/40" style="z-index:101">
    <div class="flex items-center text-xl text-white">
      <div class="fa fa-spinner mr-2 animate-spin"></div>
      Loading...
    </div>
  </div>
{/if}
{#if $removingBg}
  <div class="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-zinc-900/40" style="z-index:101">
    <div class="flex items-center text-xl text-white">
      <div class="fa fa-spinner mr-2 animate-spin"></div>
      Removing background and upscaling
    </div>
  </div>
{/if}

<div id="tempStoreImage" style="display: none;" />

<style lang="postcss" global>
  #thumbArea {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    /* grid-template-rows: 65px 1fr; */
    grid-template-rows: calc(100% - 68px);
    /* @apply bg-gray-200; */
  }
  .container-div {
    @apply flex flex-grow flex-col items-center justify-center;
  }

  .highlighted-element {
    box-shadow: 0 0 0 2px #1c73f7 !important;
    cursor: move;
  }
  #vidcontrol {
    position: static !important;
  }
  #tempStoreImage {
    padding: 10px;
    z-index: 99999999999999;
    position: relative;
    background: #fff;
  }

  .ql-editor {
    cursor: text;
    outline-width: 0px;
  }
</style>
